import { useCallback, useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useMutation, useSuspenseQuery } from '@tanstack/react-query';
import { IDNow2, type IdNowStatus } from '#providers/components/idnow2';
import { useToast } from '@repo/design-system-kit/components/toast/provider/use-toast';
import { Button } from '@repo/design-system-kit/components/button/button';
import { CFT } from '@repo/runtime-kit/sentry/sentry-tags';
import { useSentry } from '@repo/runtime-kit/sentry/context';
import { useConfig } from '@repo/shared-kit/config/config-provider';
import { useReadinessSpan } from '@repo/shared-kit/hooks/use-readiness-span';
import type { ResponseError } from '@repo/api-kit/queries/utils';
import {
  kycMutations,
  KycProcessStatus,
  KycProvider,
  kycQueries,
} from '@repo/api-kit/queries/due-diligence/kyc';
import { useKycContext } from '../context/kyc-context';
import { useKycCompletion } from '#hooks/use-kyc-completion';
import { useProviderDetails } from '#hooks/use-provider-details';
import { useKycTracking } from '#tracking/use-kyc-tracking';
import { toIdNowErrorKey } from '#utils/to-idnow-error-key';
import { SEND_SMS_TEMPLATE, TOAST_TIMEOUT } from '#constants';
import { KycError } from '#error/error';
import { IdnowLoading } from '#loading/idnow-loading';
import styles from './idnow-provider.module.css';

export function IdnowProvider() {
  const intl = useIntl();
  const toasts = useToast();
  const sentry = useSentry();
  const events = useKycTracking();
  const { environment } = useConfig();
  const canSkipKyc = !environment.isProduction;
  const {
    hasGermanResidencePermit,
    onBack,
    onComplete,
    onError,
    membershipId,
    legalCountry,
    isRegisterFlow,
    trackingPrefix,
    sendSmsMutation,
  } = useKycContext();
  const market = isRegisterFlow ? legalCountry : undefined;
  // Polling is driven by the caller (e.g. register flow polls via refetchProviderDetails;
  // non-register flows set their own refetchInterval on the parent query).
  const {
    data: kycInfo,
    refetch: refetchAcceptance,
    error: kycError,
    isError: isKycError,
  } = useSuspenseQuery(kycQueries.item({ market }));

  const handleSmsError = useCallback(
    (error: ResponseError) => {
      if (error.status === 429) {
        return toasts.showToast(
          {
            type: 'error',
            text: intl.formatMessage({
              id: 'identity-verification-kit.kyc.toasts.download-link-sms.rate-limit',
              defaultMessage:
                "We've already sent you an SMS with a link. If you haven't received it, try again in a few minutes.",
            }),
          },
          { timeout: TOAST_TIMEOUT }
        );
      }

      return toasts.showToast(
        {
          type: 'error',
          text: intl.formatMessage({
            id: 'identity-verification-kit.kyc.toasts.download-link-sms.error',
            defaultMessage:
              'We encountered a technical issue while sending you the SMS. Could you try again?',
          }),
        },
        { timeout: TOAST_TIMEOUT }
      );
    },
    [intl, toasts]
  );

  const handleSmsSuccess = useCallback(() => {
    events.idnowSmsSent();

    return toasts.showToast(
      {
        type: 'success',
        text: intl.formatMessage({
          id: 'identity-verification-kit.kyc.toasts.download-link-sms.success',
          defaultMessage: 'We sent you an SMS with a link.',
        }),
      },
      { timeout: TOAST_TIMEOUT }
    );
  }, [events, intl, toasts]);

  const sendKycSms = useMutation({
    ...(sendSmsMutation ? sendSmsMutation() : kycMutations.sendMembershipKycSms()),
    onError: handleSmsError,
    onSuccess: handleSmsSuccess,
  });

  const {
    data: providerDetails,
    refetch: refetchProviderDetails,
    error: providerDetailsError,
    isLoading,
  } = useProviderDetails({
    provider: KycProvider.idnow,
    identityVerificationId: kycInfo.activeProcess?.identityVerificationId,
    refetchAcceptance,
  });

  useEffect(() => {
    if (providerDetailsError) {
      onError?.(providerDetailsError);
    }
  }, [providerDetailsError, onError]);

  const processStatus = kycInfo.activeProcess?.processStatus;

  useReadinessSpan('kyc.idnow', {
    isLoading,
    isError: !providerDetails || !providerDetails.token || isKycError,
    error: (kycError ?? !providerDetails) ? new Error('Missing IDnow details') : undefined,
  });

  const handleTryAgain = useCallback(() => {
    events.idnowRegenerateLinkClicked();
    void refetchProviderDetails();
  }, [events, refetchProviderDetails]);

  const handleWaitingResults = useCallback(() => {
    events.idnowWaitingResultsScreenLoaded();
  }, [events]);

  const handleRejected = useCallback(() => {
    events.idnowRejectedScreenLoaded();
  }, [events]);

  const handleSendSMS = useCallback(async () => {
    await sendKycSms.mutateAsync({ templateName: SEND_SMS_TEMPLATE.IDNOW_APP_LINK });
  }, [sendKycSms]);

  useKycCompletion(processStatus, () => onComplete(KycProvider.idnow), providerDetails?.status);

  if (processStatus === KycProcessStatus.submitted) return null;

  if (isLoading) return <IdnowLoading isFullHeight={false} />;

  if (!providerDetails || !providerDetails.token) {
    sentry.captureException(new Error('IDnow KYC credentials missing'), {
      extra: {
        kycCurrentStatus: kycInfo.kycCurrentStatus,
        processStatus: kycInfo.activeProcess?.processStatus,
        membershipId,
        hasProviderDetails: Boolean(providerDetails),
      },
      tags: {
        component: 'IdnowProvider',
        provider: KycProvider.idnow,
      },
      cft: CFT.IDENTITY_VERIFICATION,
    });

    return <KycError onRetry={refetchProviderDetails} />;
  }

  const { token, reason, status } = providerDetails;

  const qrCodeUrl = `${window.location.origin}/download-idnow2?type=qr-code&id=${token}`;

  return (
    <>
      <div className={styles.idnow2}>
        <IDNow2
          hasGermanResidencePermit={hasGermanResidencePermit}
          onBack={onBack}
          onRejected={handleRejected}
          onSendSMS={handleSendSMS}
          onWaitingResults={handleWaitingResults}
          qrCodeUrl={qrCodeUrl}
          reason={toIdNowErrorKey(reason)}
          // oxlint-disable-next-line typescript/consistent-type-assertions
          status={status as IdNowStatus}
          token={token}
          trackingPrefix={trackingPrefix}
          tryAgain={handleTryAgain}
        />
      </div>
      {canSkipKyc ? (
        <Button
          className={styles.qaButton}
          onClick={() => {
            onComplete(KycProvider.idnow);
          }}
          stretch
          type="button"
          variant="secondary"
        >
          <FormattedMessage
            defaultMessage="QA Tool | Complete KYC"
            id="identity-verification-kit.kyc.skip.cta"
          />
        </Button>
      ) : null}
    </>
  );
}
